import Component from '@glimmer/component';
import { getDashComponent } from 'tio-employee/utils/wellness-insights';
import { VStack } from 'tio-ui/components/layout';
import { Section } from 'tio-ui/components/layout';
import type { WellnessTrainExtension as WellnessTrainModel } from 'tio-data/data/resources/wellness-train.ext';
import { t } from 'ember-intl';
import type { Store as StoreService } from '@warp-drive/core';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import RouterService from '@ember/routing/router-service';
import Modal from 'tio-ui/components/modal';
import type WellnessInsightsController from 'tio-employee/controllers/authenticated/wellness-insights';
import RadioGroup from 'tio-ui/components/radio-group';
import Form from 'tio-ui/components/form';
import Textarea from 'tio-ui/components/textarea';
import type { FormResult } from 'tio-ui/components/form';
import { ACCOUNT_DECLINED_REASON } from 'tio-data/data/resources/answer-profile.constants';
import type { AnswerProfileExtension as AnswerProfileModel } from 'tio-data/data/resources/answer-profile.ext';
import Button from 'tio-ui/components/button';
import type SessionContextService from 'tio-employee/services/session-context';
import { not, and, or } from 'tio-ui/utilities';
import { reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import UploadInfoRec from 'tio-employee/components/observability/upload-info-rec';
import EmptyState from 'tio-ui/components/empty-state';
import PageHero from 'tio-ui/components/page-hero';
import { PhLightbulbFilament } from 'ember-phosphor-icons';
import * as v from 'valibot';

const AnswerProfileSchema = v.pipe(
  v.object({
    accountDeclinedReason: v.nonNullable(v.string(), 'Reason not provided'),
    accountDeclinedComment: v.nullable(v.pipe(v.string(), v.nonEmpty())),
  }),
  v.forward(
    v.check((input) => {
      const reasonIsOther = input.accountDeclinedReason === ACCOUNT_DECLINED_REASON.other;
      const commentMissing = !input.accountDeclinedComment;
      return !(reasonIsOther && commentMissing);
    }, "Comment not provided with reason: 'Other'"),
    ['accountDeclinedComment']
  ),
  v.transform((input) => {
    const { accountDeclinedReason, accountDeclinedComment } = input;
    const reasonIsKnown = accountDeclinedReason !== ACCOUNT_DECLINED_REASON.other;
    const nullifiedComment = reasonIsKnown ? null : accountDeclinedComment;
    return { ...input, accountDeclinedComment: nullifiedComment };
  })
);

interface S {
  Args: {
    controller: WellnessInsightsController;
    model: WellnessTrainModel;
  };
}

type DeclineAccountFormData = {
  accountDeclinedReason?: string;
  accountDeclinedComment?: string | null;
};

export default class WellnessInsightsIndex extends Component<S> {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked loading = false;

  get components() {
    return this.args.model?.componentDefinitions ?? [];
  }

  get hasScoreAndSummary() {
    return !!(this.args.model?.scoreDefinition && this.args.model?.summaryDefinition);
  }

  get accountCreationFeedbackActive() {
    return !!this.args.controller.accountCreationFeedbackActive;
  }

  get answerProfile() {
    return this.args.model?.user?.answerProfile;
  }

  get formProperties(): DeclineAccountFormData {
    const profile = this.answerProfile;
    if (!profile) return {};
    return {
      accountDeclinedReason: profile.accountDeclinedReason,
      accountDeclinedComment: profile.accountDeclinedComment,
    };
  }

  get canDeclineAccount() {
    return !!this.args.model?.accountPromptDefinition;
  }

  get answerProfileValid() {
    return !!this.formProperties && v.is(AnswerProfileSchema, this.formProperties);
  }

  resetAnswerProfile = () => {
    if (!this.args.model) {
      return;
    }
    const { user } = this.args.model;
    const { answerProfile } = user;

    if (answerProfile.isNew) {
      answerProfile.unloadRecord();
      user.answerProfile = this.store.createRecord<AnswerProfileModel>('answer-profile', {
        providingApp: 'tio-employee',
      });
    } else {
      answerProfile.rollbackAttributes();
    }
  };

  closeAccountCreationFeedback = () => {
    this.args.controller.set('accountCreationFeedbackActive', '');
    this.resetAnswerProfile();
  };

  onDeclineAccountFormChange = async (
    _result: FormResult<DeclineAccountFormData>
  ): Promise<void> => {
    if (this.answerProfile) {
      Object.assign(this.answerProfile, _result.data);
    }
  };

  setAccountDeclinedReason = (value: string) => {
    if (this.answerProfile) {
      // The select only emits ACCOUNT_DECLINED_REASON values; narrow the string
      // to the field's const-union to match the schema-typed attribute.
      this.answerProfile.accountDeclinedReason =
        value as (typeof ACCOUNT_DECLINED_REASON)[keyof typeof ACCOUNT_DECLINED_REASON];
    }
  };

  onDeclineAccountFormSubmit = async (
    _result: FormResult<DeclineAccountFormData>,
    _event?: SubmitEvent
  ) => {
    const result = v.safeParse(AnswerProfileSchema, this.formProperties);
    if (result.success) {
      if (this.answerProfile) {
        Object.assign(this.answerProfile, result.output);
      }
      this.loading = true;
      if (this.answerProfile) {
        await saveRecord(this.store, this.answerProfile);
      }
      // Standalone refresh of the route model (NOT after a builder mutation on
      // this record — the save above is on `answerProfile`) — keep the refetch
      // via the helper.
      if (this.args.model) {
        await reloadRecord(this.store, this.args.model);
      }
      this.args.controller.set('accountCreationFeedbackActive', '');
      this.loading = false;
    }
  };

  <template>
    {{! A section landing page, so the route title takes the hero treatment
        (page-header.instructions.md § Which treatment). One header and one
        root stack for both states, so the empty state cannot grow a second,
        differently-shaped header or a different page rhythm. }}
    <VStack class="gap-8">
      <PageHero
        @title={{t "wellness_insights.student_loan_insights.title"}}
        @subtitle={{t "wellness_insights.student_loan_insights.hero_subtitle"}}
        @icon={{PhLightbulbFilament}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      >
        {{! The score is graded state, which DESIGN.md § Page composition puts in
            the hero so the sections below can keep neutral headings. }}
        {{#if @model.scoreDefinition}}
          <div class="mt-6 max-w-xs">
            {{#let (getDashComponent @model.scoreDefinition) as |ScoreComponent|}}
              <ScoreComponent @componentDefinition={{@model.scoreDefinition}} />
            {{/let}}
          </div>
        {{/if}}
      </PageHero>
      {{#if @model}}
        {{#if this.hasScoreAndSummary}}
          <Section>
            <:header>{{t "common.status"}}</:header>
            <:body>
              <VStack>
                {{#if @model.summaryDefinition}}
                  {{#let (getDashComponent @model.summaryDefinition) as |SummaryComponent|}}
                    <SummaryComponent @componentDefinition={{@model.summaryDefinition}} />
                  {{/let}}
                {{/if}}
                <UploadInfoRec />
              </VStack>
            </:body>
          </Section>
        {{else if @model.accountPromptDefinition}}
          <Section>
            {{#let
              (getDashComponent @model.accountPromptDefinition)
              as |AccountCreationPromptComponent|
            }}
              <AccountCreationPromptComponent
                @componentDefinition={{@model.accountPromptDefinition}}
              />
            {{/let}}
          </Section>
        {{/if}}
        {{#if @model.recommendationDefinitions.length}}
          <Section>
            <:header>{{t "wellness_insights.dashboard.recommendations_title"}}</:header>
            <:body>
              <div class="grid grid-cols-1 gap-4">
                {{#each @model.recommendationDefinitions as |definition|}}
                  {{#let (getDashComponent definition) as |DashComponent|}}
                    <DashComponent @componentDefinition={{definition}} />
                  {{/let}}
                {{/each}}
              </div>
            </:body>
          </Section>
        {{/if}}
        <Modal
          @isOpen={{and this.accountCreationFeedbackActive this.canDeclineAccount}}
          @onClose={{this.closeAccountCreationFeedback}}
        >
          <:header>{{t "wellness_insights.dashboard.decline_account_modal.title"}}</:header>
          <:body>
            <div class="tio-col">
              {{t "wellness_insights.dashboard.decline_account_modal.prompt"}}
              <Form
                @data={{this.formProperties}}
                @onChange={{this.onDeclineAccountFormChange}}
                @onSubmit={{this.onDeclineAccountFormSubmit}}
                class="tio-col"
              >
                <RadioGroup
                  data-test-id="decline-reason-radio-group"
                  @value={{this.answerProfile.accountDeclinedReason}}
                  @onChange={{this.setAccountDeclinedReason}}
                  @options={{array
                    (hash
                      key=ACCOUNT_DECLINED_REASON.doNotHave
                      label=(t
                        "wellness_insights.dashboard.decline_account_modal.form.reason.do_not_have"
                      )
                      dataTestId="radio-do-not-have"
                    )
                    (hash
                      key=ACCOUNT_DECLINED_REASON.doNotWant
                      label=(t
                        "wellness_insights.dashboard.decline_account_modal.form.reason.do_not_want"
                      )
                      dataTestId="radio-do-not-want"
                    )
                    (hash
                      key=ACCOUNT_DECLINED_REASON.other
                      label=(t
                        "wellness_insights.dashboard.decline_account_modal.form.reason.other"
                      )
                      dataTestId="radio-other"
                    )
                  }}
                />
                {{#if (eq this.answerProfile.accountDeclinedReason "other")}}
                  <Textarea
                    data-test-id="decline-comment-textarea"
                    @name="accountDeclinedComment"
                    @value={{this.answerProfile.accountDeclinedComment}}
                    placeholder={{t
                      "wellness_insights.dashboard.decline_account_modal.form.comment.placeholder"
                    }}
                  />
                {{/if}}
                <div class="tio-flex-end gap-3">
                  <Button
                    @appearance="outlined"
                    type="reset"
                    {{on "click" this.closeAccountCreationFeedback}}
                  >
                    {{t "wellness_insights.dashboard.decline_account_modal.cancel"}}
                  </Button>
                  <Button
                    type="submit"
                    @isDisabled={{or (not this.answerProfileValid) this.loading}}
                  >
                    {{t "wellness_insights.dashboard.decline_account_modal.submit"}}
                  </Button>
                </div>
              </Form>
            </div>
          </:body>
        </Modal>
      {{else}}
        {{! The route returns null when the parent has no wellness data, which is
            every account without a wellness train — an admin-only login, for
            instance. Without this the whole page rendered blank. }}
        <EmptyState
          @icon={{PhLightbulbFilament}}
          @title={{t "wellness_insights.student_loan_insights.empty.title"}}
          @description={{t "wellness_insights.student_loan_insights.empty.description"}}
          @color="violet"
        />
      {{/if}}
    </VStack>
  </template>
}
